// WorkflowRenameOutcomeModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { WORKFLOW_OUTCOME_COMPLETE } from 'workflows/workflowGraphUtils';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

/**
 * WorkflowRenameOutcomeModal
 *
 * Thin rename UI for a custom outcome path label (not Complete).
 *
 * @returns {ReactNode}
 */
const WorkflowRenameOutcomeModal = React.memo(({
  fromTask = null,
  initialLabel = '',
  isOpen = false,
  onClose,
  onSave,
}) => {
  const isMobile = useIsMobile();
  const [buttonLabel, setButtonLabel] = useState('');
  const [error, setError] = useState('');

  const fromTaskKey = String(fromTask?.key || '').trim();
  const seedLabel = String(initialLabel || '').trim();

  useEffect(() => {
    if (!isOpen) return;
    setButtonLabel(seedLabel);
    setError('');
  }, [isOpen, fromTaskKey, seedLabel]);

  const handleSave = () => {
    const cleanedLabel = String(buttonLabel || '').trim();
    if (!cleanedLabel) {
      setError('Name this path (for example, Bad Fit).');
      return;
    }
    if (cleanedLabel.toLowerCase() === WORKFLOW_OUTCOME_COMPLETE) {
      setError('Complete is already used for the main path. Choose a different name.');
      return;
    }
    onSave?.({ label: cleanedLabel });
  };

  if (!isOpen) return null;

  const fromTitle = String(fromTask?.title || '').trim() || 'this task';

  return (
    <Modal
      iconKey="edit"
      isOpen={isOpen}
      title="Rename outcome"
      width={isMobile ? '100%' : 420}
      onClose={() => onClose?.()}
    >
      <ModalForm>
        <ModalHint hint={`Operators see this as a button on “${fromTitle}”.`} />

        <ModalField>
          <ModalLabel label="Path name" />
          <TextInput
            autoFocus
            autoSelect
            error={Boolean(error)}
            placeholder="Bad Fit"
            value={buttonLabel}
            onChange={(event) => {
              setButtonLabel(String(event.target.value || ''));
              if (error) setError('');
            }}
            onEnterPress={handleSave}
          />
          {error ? (
            <ModalHint hint={error} />
          ) : null}
        </ModalField>
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="END">
          <Button
            iconKey="close"
            label="Cancel"
            onClick={() => onClose?.()}
          />
          <Button
            iconKey="check"
            label="Save"
            onClick={handleSave}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

WorkflowRenameOutcomeModal.displayName = 'WorkflowRenameOutcomeModal';

export default WorkflowRenameOutcomeModal;
